import { useState } from "react";
import { 
  X, DollarSign, Users, TrendingUp, ShoppingCart, 
  Target, Eye, MousePointer, Percent, ArrowUpRight,
  ArrowDownRight, Clock, Zap, PieChart, BarChart3
} from "lucide-react";

// Simulación de datos financieros por producto
const generateFinancialData = (product, marketSize, audienceReach) => {
  // Simular métricas basadas en el producto
  const baseInvestment = Math.round((product?.trend_score || 70) * 5 + Math.random() * 200);
  const impressions = Math.round(audienceReach * (1.5 + Math.random()));
  const reach = Math.round(impressions * 0.7);
  const clicks = Math.round(impressions * (0.02 + Math.random() * 0.03)); // CTR 2-5%
  const conversions = Math.round(clicks * (0.03 + Math.random() * 0.05)); // CR 3-8%
  const avgOrderValue = 25 + Math.random() * 35;
  const revenue = conversions * avgOrderValue;
  const adSpend = baseInvestment;
  const profit = revenue - adSpend;
  const roas = revenue / adSpend;
  const cpa = adSpend / (conversions || 1);
  const ctr = (clicks / impressions) * 100;
  const conversionRate = (conversions / clicks) * 100;
  
  return {
    // Inversión
    adSpend,
    dailyBudget: Math.round(adSpend / 7),
    daysRunning: 7,
    
    // Alcance e Impacto
    impressions,
    reach,
    frequency: (impressions / reach).toFixed(1),
    
    // Engagement
    clicks,
    ctr: ctr.toFixed(2),
    
    // Conversiones
    conversions,
    conversionRate: conversionRate.toFixed(2),
    cpa: cpa.toFixed(2),
    
    // Financiero
    revenue: revenue.toFixed(2),
    avgOrderValue: avgOrderValue.toFixed(2),
    profit: profit.toFixed(2),
    roas: roas.toFixed(2),
    profitMargin: ((profit / revenue) * 100).toFixed(1),
    
    // Ritmo
    salesPerDay: (conversions / 7).toFixed(1),
    revenuePerDay: (revenue / 7).toFixed(2),
    
    // Proyección
    projectedMonthlyRevenue: (revenue * 4.3).toFixed(2),
    projectedMonthlyProfit: (profit * 4.3).toFixed(2)
  };
};

export default function FinancialMetricsModal({ 
  isOpen, 
  onClose, 
  product, 
  marketSize = 240000, 
  audienceReach = 36000 
}) {
  const [timeRange, setTimeRange] = useState('7d');
  
  if (!isOpen) return null;
  
  const data = generateFinancialData(product, marketSize, audienceReach);
  
  const isProfit = parseFloat(data.profit) > 0;
  const isGoodRoas = parseFloat(data.roas) >= 2;
  const isGoodCtr = parseFloat(data.ctr) >= 2;
  const isGoodConversion = parseFloat(data.conversionRate) >= 3;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-2 sm:p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-4xl my-2 sm:my-4">
          
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10 rounded-t-2xl">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white">Panel Financiero</h2>
                <p className="text-xs sm:text-sm text-zinc-400">{product?.name || "Producto"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Selector de tiempo */}
              <div className="flex bg-zinc-800 rounded-lg p-1">
                {['7d', '14d', '30d'].map(range => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-2 sm:px-3 py-1 rounded text-xs font-medium transition-all ${
                      timeRange === range
                        ? 'bg-emerald-500 text-black'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-4 space-y-4">
            
            {/* Resumen Principal */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Inversión */}
              <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-zinc-400">Invertido</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-red-400">
                  ${data.adSpend}
                </div>
                <div className="text-xs text-zinc-500">${data.dailyBudget}/día × {data.daysRunning} días</div>
              </div>
              
              {/* Revenue */}
              <div className="bg-zinc-800/50 rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-zinc-400">Ingresos</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-emerald-400">
                  ${data.revenue}
                </div>
                <div className="text-xs text-zinc-500">${data.revenuePerDay}/día</div>
              </div>
              
              {/* Beneficio */}
              <div className={`rounded-xl p-3 sm:p-4 ${isProfit ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {isProfit ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                  <span className="text-xs text-zinc-400">Beneficio</span>
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                  ${data.profit}
                </div>
                <div className="text-xs text-zinc-500">{data.profitMargin}% margen</div>
              </div>
              
              {/* ROAS */}
              <div className={`rounded-xl p-3 sm:p-4 ${isGoodRoas ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-zinc-400">ROAS</span>
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${isGoodRoas ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {data.roas}x
                </div>
                <div className="text-xs text-zinc-500">{isGoodRoas ? 'Rentable' : 'Optimizar'}</div>
              </div>
            </div>

            {/* Embudo de Conversión */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-blue-400" />
                Embudo de Conversión
              </h3>
              
              <div className="space-y-3">
                {/* Impresiones */}
                <div className="flex items-center gap-3">
                  <div className="w-24 text-xs text-zinc-400">Impresiones</div>
                  <div className="flex-1 bg-zinc-800 rounded-full h-6 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-blue-500 rounded-full" style={{ width: '100%' }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {data.impressions.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-16 text-xs text-zinc-500 text-right">100%</div>
                </div>
                
                {/* Alcance */}
                <div className="flex items-center gap-3">
                  <div className="w-24 text-xs text-zinc-400">Alcance</div>
                  <div className="flex-1 bg-zinc-800 rounded-full h-6 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-cyan-500 rounded-full" style={{ width: '70%' }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {data.reach.toLocaleString()} personas
                    </span>
                  </div>
                  <div className="w-16 text-xs text-zinc-500 text-right">70%</div>
                </div>
                
                {/* Clicks */}
                <div className="flex items-center gap-3">
                  <div className="w-24 text-xs text-zinc-400">Clicks</div>
                  <div className="flex-1 bg-zinc-800 rounded-full h-6 relative overflow-hidden">
                    <div className={`absolute inset-y-0 left-0 rounded-full ${isGoodCtr ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(parseFloat(data.ctr) * 10, 100)}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {data.clicks.toLocaleString()} ({data.ctr}% CTR)
                    </span>
                  </div>
                  <div className={`w-16 text-xs text-right ${isGoodCtr ? 'text-emerald-400' : 'text-amber-400'}`}>{data.ctr}%</div>
                </div>
                
                {/* Conversiones */}
                <div className="flex items-center gap-3">
                  <div className="w-24 text-xs text-zinc-400">Ventas</div>
                  <div className="flex-1 bg-zinc-800 rounded-full h-6 relative overflow-hidden">
                    <div className={`absolute inset-y-0 left-0 rounded-full ${isGoodConversion ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(parseFloat(data.conversionRate) * 5, 100)}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {data.conversions} ventas ({data.conversionRate}% CR)
                    </span>
                  </div>
                  <div className={`w-16 text-xs text-right ${isGoodConversion ? 'text-emerald-400' : 'text-amber-400'}`}>{data.conversionRate}%</div>
                </div>
              </div>
            </div>

            {/* Métricas Detalladas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Impacto */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-cyan-400" />
                  Impacto Publicitario
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Impresiones totales</span>
                    <span className="text-sm font-medium text-white">{data.impressions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Personas alcanzadas</span>
                    <span className="text-sm font-medium text-cyan-400">{data.reach.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Frecuencia promedio</span>
                    <span className="text-sm font-medium text-white">{data.frequency}x por persona</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Costo por 1,000 imp. (CPM)</span>
                    <span className="text-sm font-medium text-white">${(data.adSpend / data.impressions * 1000).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Ventas */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-emerald-400" />
                  Rendimiento de Ventas
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Total ventas</span>
                    <span className="text-sm font-medium text-emerald-400">{data.conversions} unidades</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Ticket promedio</span>
                    <span className="text-sm font-medium text-white">${data.avgOrderValue}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Ritmo de ventas</span>
                    <span className="text-sm font-medium text-white">{data.salesPerDay} ventas/día</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Costo por venta (CPA)</span>
                    <span className="text-sm font-medium text-amber-400">${data.cpa}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Proyección */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-400" />
                Proyección Mensual (si mantienes el ritmo actual)
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="text-xl font-bold text-emerald-400">${data.projectedMonthlyRevenue}</div>
                  <div className="text-xs text-zinc-500">Ingresos/mes</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-cyan-400">${data.projectedMonthlyProfit}</div>
                  <div className="text-xs text-zinc-500">Beneficio/mes</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{Math.round(parseFloat(data.salesPerDay) * 30)}</div>
                  <div className="text-xs text-zinc-500">Ventas/mes</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-amber-400">${Math.round(parseFloat(data.dailyBudget) * 30)}</div>
                  <div className="text-xs text-zinc-500">Inversión/mes</div>
                </div>
              </div>
            </div>

            {/* Resumen Ejecutivo */}
            <div className={`rounded-xl p-4 ${isProfit && isGoodRoas ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
              <h3 className="text-sm font-semibold text-white mb-2">
                {isProfit && isGoodRoas ? '✅ Resumen: RENTABLE' : '⚠️ Resumen: OPTIMIZAR'}
              </h3>
              <p className="text-sm text-zinc-300">
                {isProfit && isGoodRoas 
                  ? `Por cada $1 invertido generas $${data.roas}. De ${data.reach.toLocaleString()} personas impactadas, ${data.conversions} compraron (${data.conversionRate}% conversión). Beneficio neto: $${data.profit}.`
                  : `ROAS de ${data.roas}x está por debajo del objetivo (2x). Considera optimizar el targeting o el creativo del anuncio.`
                }
              </p>
            </div>

          </div>

          {/* Footer */}
          <div className="p-3 sm:p-4 border-t border-zinc-800 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
